import { useState, useEffect, useCallback } from 'react'

/**
 * `useMediaQuery` is a hook for responding to CSS media queries in JavaScript.
 * It evaluates a media query string and returns a boolean indicating whether the query matches.
 *
 * @param {String} query - The CSS media query string.
 * @return {Boolean} - A boolean value indicating if the media query matches.
 */

const getMatches = (mediaQuery) => {
  if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
    return false
  }

  return window.matchMedia(mediaQuery).matches
}

export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(getMatches(query))

  const handleChange = useCallback((event) => {
    setMatches(event.matches)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return
    }

    const mediaQueryList = window.matchMedia(query)
    mediaQueryList.addEventListener('change', handleChange)

    return () => {
      mediaQueryList.removeEventListener('change', handleChange)
    }
  }, [query, handleChange])

  return matches
}
